import { appBackendApiClient } from './apiClient';
import {
  AdminChallenge,
  AdminChallengeDetail,
  AdminChallengeFilters,
  AdminChallengeFormValues,
} from '../types';

const mapChallenge = (raw: any): AdminChallenge => ({
  id: raw.id,
  title: raw.title,
  description: raw.description,
  causeId: raw.causeId,
  targetAmount: raw.targetAmount,
  actualAmount: raw.actualAmount,
  percentage: Number(raw.percentage ?? 0),
  contributionsCount: Number(raw.contributionsCount ?? 0),
  currency: raw.currency || 'EGP',
  startDate: raw.startDate,
  endDate: raw.endDate,
  coverPhotoUrl: raw.coverPhotoUrl ?? null,
  status: raw.status,
  createdById: raw.createdById,
  creator: {
    userId: raw.creator?.userId ?? raw.createdById,
    displayName: raw.creator?.displayName ?? null,
  },
  deletedAt: raw.deletedAt ?? null,
  createdAt: raw.createdAt,
  updatedAt: raw.updatedAt,
});

const mapChallengeDetail = (raw: any): AdminChallengeDetail => ({
  ...mapChallenge(raw),
  contributions: Array.isArray(raw.contributions)
    ? raw.contributions.map((c: any) => ({
        id: c.id,
        donationId: c.donationId,
        userId: c.userId,
        displayName: c.displayName ?? null,
        amount: c.amount,
        currency: c.currency,
        isAnonymous: Boolean(c.isAnonymous),
        createdAt: c.createdAt,
      }))
    : [],
});

export const ChallengeService = {
  async getChallenges(filters: AdminChallengeFilters = {}): Promise<AdminChallenge[]> {
    const params: Record<string, string | boolean> = {};
    if (filters.status) params.status = filters.status;
    if (filters.causeId) params.causeId = filters.causeId;
    if (filters.createdById) params.createdById = filters.createdById;
    if (filters.includeDeleted) params.includeDeleted = true;

    const response = await appBackendApiClient.get<any[]>('/admin/challenges', { params });
    return (Array.isArray(response.data) ? response.data : []).map(mapChallenge);
  },

  async getChallengeById(id: string): Promise<AdminChallengeDetail> {
    const response = await appBackendApiClient.get<any>(`/admin/challenges/${id}`);
    return mapChallengeDetail(response.data);
  },

  async createChallenge(values: AdminChallengeFormValues): Promise<AdminChallenge> {
    const response = await appBackendApiClient.post<any>('/admin/challenges', {
      title: values.title,
      description: values.description,
      causeId: values.causeId,
      targetAmount: Number(values.targetAmount),
      startDate: values.startDate,
      endDate: values.endDate,
      coverPhotoUrl: values.coverPhotoUrl || undefined,
    });
    return mapChallenge(response.data);
  },

  async updateChallenge(id: string, values: Partial<AdminChallengeFormValues>): Promise<AdminChallenge> {
    const payload: Record<string, unknown> = {};
    if (values.title !== undefined) payload.title = values.title;
    if (values.description !== undefined) payload.description = values.description;
    if (values.causeId !== undefined) payload.causeId = values.causeId;
    if (values.targetAmount !== undefined && values.targetAmount !== '') payload.targetAmount = Number(values.targetAmount);
    if (values.startDate !== undefined) payload.startDate = values.startDate;
    if (values.endDate !== undefined) payload.endDate = values.endDate;
    if (values.coverPhotoUrl !== undefined) payload.coverPhotoUrl = values.coverPhotoUrl;
    if (values.status !== undefined) payload.status = values.status;

    const response = await appBackendApiClient.patch<any>(`/admin/challenges/${id}`, payload);
    return mapChallenge(response.data);
  },

  async deleteChallenge(id: string): Promise<void> {
    await appBackendApiClient.delete(`/admin/challenges/${id}`);
  },
};
